import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAddressSuggestions,
  fetchSecondaryAddressSuggestions,
  type SmartySuggestion,
} from '@Services/smarty/smarty.service';

const EMPTY_ARRAY: SmartySuggestion[] = [];

export const useAddressAutocomplete = () => {
  // `searchQuery` is the text the user has typed. It is reused as the `search`
  // value on the secondary-expansion call, which Smarty requires to be
  // non-empty. `drillDown` intentionally leaves it untouched.
  const [searchQuery, setSearchQuery] = useState('');
  const [secondaryParent, setSecondaryParent] = useState<SmartySuggestion | null>(null);

  const { data: primaryResults = EMPTY_ARRAY, isFetching: isSearching } = useQuery({
    queryKey: ['smarty-autocomplete', searchQuery],
    queryFn: () => fetchAddressSuggestions(searchQuery),
    enabled: searchQuery.length >= 3,
    retry: false,
  });

  const { data: secondaryResults = EMPTY_ARRAY, isFetching: isLoadingSecondary } = useQuery({
    queryKey: ['smarty-secondary', secondaryParent, searchQuery],
    queryFn: () => fetchSecondaryAddressSuggestions(secondaryParent!, searchQuery),
    enabled: !!secondaryParent,
    retry: false,
  });

  const search = (query: string) => {
    setSecondaryParent(null);
    setSearchQuery(query);
  };

  const drillDown = (parent: SmartySuggestion) => {
    setSecondaryParent(parent);
  };

  const suggestions = secondaryParent ? secondaryResults : primaryResults;
  const isLoading = isSearching || isLoadingSecondary;

  return { suggestions, isLoading, search, drillDown };
};
